import { normalizePointBalance, type PointBalance } from '@/shared/model/point-balance'
import { resolveAiWatermarkValueFromPayload } from './ai-watermark'

export type TeamGroupKey = 'personal' | 'created' | 'joined' | 'all'

export interface TeamItem {
  teamId: string
  name: string
  avatar: string
  type: string
  teamKind: string
  currentPlanType: string
  planName: string
  planLevel: number | null
  planStatus: string
  expireTime: number
  isDefaultTeam: boolean
  isPersonalTeam: boolean
  collaborationEnabled: boolean
  concurrencyLimit: number | null
  memberRole: string
  memberCount: number | null
  memberLimitEdit: number | null
  // 额外算力相关字段（通常仅当前团队会有）
  pointLimitMonth?: number
  pointUsedMonth?: number
  pointRemainMonth?: number
  teamPointLimitMonth?: number
  pointBalancePermanent?: number
  pointBalance?: PointBalance
  aiWatermark?: number
}

export interface EnterTeamResult {
  teamId: string
  teamName: string
  teamAvatar?: string
  hasTeamAvatarField?: boolean
  teamKind?: string
  currentPlanType?: string
  planName?: string
  planStatus?: string
  concurrencyLimit?: number
  isPersonalTeam?: boolean
  collaborationEnabled?: boolean
  memberRole: string
  graceExpireTime?: number
  seatStatus?: string
  hasValidSeat?: boolean
  enterMode?: string
  pointLimitMonth?: number
  pointUsedMonth?: number
  pointRemainMonth?: number
  teamPointLimitMonth?: number
  pointBalancePermanent?: number
  pointBalance?: PointBalance
  aiWatermark?: number
}

export interface TeamGroup {
  key: TeamGroupKey
  label: string
  teams: readonly TeamItem[]
}

const PERSONAL_GROUP_LABEL = '个人账号'
const CREATED_GROUP_LABEL = '我创建的团队'
const JOINED_GROUP_LABEL = '我加入的团队'

const CREATED_GROUP_KEYS = [
  'create_list',
  'created_list',
  'create',
  'created',
  'owner_list',
  'own_list',
  'my_create',
  'my_created',
]

const JOINED_GROUP_KEYS = [
  'join_list',
  'joined_list',
  'join',
  'joined',
  'member_list',
  'my_join',
  'my_joined',
]

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

function readString(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return ''
  }

  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value)
    }
  }

  return ''
}

function readNumber(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return null
  }

  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }

    if (typeof value === 'string') {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }

  return null
}

function readFlag(record: Record<string, unknown> | null, keys: string[]) {
  const value = readNumber(record, keys)
  return value === 1
}

function readArray(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return []
  }

  for (const key of keys) {
    const value = record[key]
    if (Array.isArray(value)) {
      return value
    }
  }

  return []
}

function dedupeTeams(teams: TeamItem[]) {
  const seen = new Set<string>()
  const result: TeamItem[] = []

  for (const team of teams) {
    if (seen.has(team.teamId)) {
      continue
    }

    seen.add(team.teamId)
    result.push(team)
  }

  return result
}

export function getTeamPlanLabel(team: TeamItem) {
  const normalizedPlanName = team.planName.trim()
  const normalizedPlanStatus = team.planStatus.trim().toLowerCase()
  const normalizedPlanType = team.currentPlanType.trim().toLowerCase()

  if (normalizedPlanStatus.includes('expire') || normalizedPlanStatus.includes('过期')) {
    return '套餐已过期'
  }

  if (normalizedPlanName) {
    return normalizedPlanName
  }

  if (team.planLevel !== null && team.planLevel > 0) {
    return `V${team.planLevel}`
  }

  if (normalizedPlanType === 'team') {
    return '团队会员'
  }

  if (normalizedPlanType === 'personal') {
    return '个人会员'
  }

  return '免费版'
}

export function normalizeTeamItem(value: unknown): TeamItem | null {
  const record = asRecord(value)
  const teamId = readString(record, ['team_id', 'id'])

  if (!teamId) {
    return null
  }

  return {
    teamId,
    name: readString(record, ['name', 'team_name']) || '未命名团队',
    avatar: readString(record, ['team_avatar', 'avatar', 'cover']),
    type: readString(record, ['type']),
    teamKind: readString(record, ['team_kind', 'kind']),
    currentPlanType: readString(record, ['current_plan_type', 'plan_type']),
    planName: readString(record, ['plan_name', 'current_plan_name']),
    planLevel: readNumber(record, ['current_plan_level', 'plan_level', 'level']),
    planStatus: readString(record, ['plan_status', 'status_text']),
    expireTime: readNumber(record, ['expire_time']) ?? 0,
    isDefaultTeam: readFlag(record, ['is_default_team']),
    isPersonalTeam:
      readFlag(record, ['is_personal_team']) ||
      readString(record, ['team_kind', 'kind']).trim().toLowerCase() === 'personal',
    collaborationEnabled: readFlag(record, ['collaboration_enabled']),
    concurrencyLimit: readNumber(record, ['concurrency_limit']),
    memberRole: readString(record, ['member_role', 'role']),
    memberCount: readNumber(record, ['member_count']),
    memberLimitEdit: readNumber(record, ['member_limit_edit', 'member_limit', 'member_limit_total']),
    pointLimitMonth: readNumber(record, ['point_limit_month', 'month_point_limit', 'month_quota_total', 'quota_total']) ?? undefined,
    pointUsedMonth: readNumber(record, ['point_used_month', 'month_point_used', 'month_quota_used', 'quota_used']) ?? undefined,
    pointRemainMonth: readNumber(record, ['point_remain_month', 'month_point_remain', 'point_remain', 'point']) ?? undefined,
    teamPointLimitMonth: readNumber(record, ['team_point_limit_month', 'team_point_total', 'point_total']) ?? undefined,
    pointBalancePermanent: readNumber(record, ['point_balance_permanent', 'team_point_total', 'point_total']) ?? undefined,
    pointBalance: normalizePointBalance(record?.point_balance),
    aiWatermark: resolveAiWatermarkValueFromPayload(record) ?? undefined,
  }
}

function normalizeTeamList(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return dedupeTeams(
    value
      .map((item) => normalizeTeamItem(item))
      .filter((item): item is TeamItem => Boolean(item)),
  )
}

function buildGroupsFromRecord(record: Record<string, unknown> | null) {
  const personalTeam = normalizeTeamItem(record?.personal)
  const createdTeams = normalizeTeamList(readArray(record, CREATED_GROUP_KEYS))
  const joinedTeams = normalizeTeamList(readArray(record, JOINED_GROUP_KEYS))

  const groups: TeamGroup[] = []

  if (personalTeam) {
    groups.push({ key: 'personal', label: PERSONAL_GROUP_LABEL, teams: [personalTeam] })
  }

  if (createdTeams.length > 0) {
    groups.push({ key: 'created', label: CREATED_GROUP_LABEL, teams: createdTeams })
  }

  if (joinedTeams.length > 0) {
    groups.push({ key: 'joined', label: JOINED_GROUP_LABEL, teams: joinedTeams })
  }

  return groups
}

export function normalizeTeamGroups(payload: unknown) {
  const record = asRecord(payload)
  const nestedCandidates = [
    record,
    asRecord(record?.data),
    asRecord(record?.list),
    asRecord(record?.result),
  ]

  for (const candidate of nestedCandidates) {
    const groups = buildGroupsFromRecord(candidate)
    if (groups.length > 0) {
      return groups
    }
  }

  const fallbackList = normalizeTeamList(
    Array.isArray(payload)
      ? payload
      : Array.isArray(record?.list)
        ? record?.list
        : Array.isArray(record?.data)
          ? record?.data
          : [],
  )

  if (fallbackList.length === 0) {
    return []
  }

  return [{ key: 'all', label: '团队列表', teams: fallbackList } satisfies TeamGroup]
}

export function flattenTeamGroups(groups: readonly TeamGroup[]) {
  return groups.flatMap((group) => group.teams)
}

export function pickInitialTeamId(groups: readonly TeamGroup[], preferredTeamId = '') {
  const allTeams = flattenTeamGroups(groups)

  if (preferredTeamId && allTeams.some((team) => team.teamId === preferredTeamId)) {
    return preferredTeamId
  }

  return allTeams.find((team) => team.isDefaultTeam)?.teamId ?? allTeams[0]?.teamId ?? ''
}
